/**
 * Client-side document parsing for upload. Produces the text the extraction
 * engine consumes, with `\f` between PDF pages so provision page numbers map
 * back to the source. PDF parsing loads pdfjs lazily to keep the main bundle
 * inside the §17.5 budget.
 */

export interface ParsedFile {
  text: string;
  pageCount: number;
  wordCount: number;
  sha256: string;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

async function parsePdf(buffer: ArrayBuffer): Promise<{ text: string; pageCount: number }> {
  const pdfjs = await import("pdfjs-dist");
  const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const line: string[] = [];
    for (const item of content.items) {
      if ("str" in item) line.push(item.str);
    }
    pages.push(line.join(" "));
  }
  await doc.destroy();
  return { text: pages.join("\f"), pageCount: doc.numPages };
}

export async function parseFile(file: File): Promise<ParsedFile> {
  const buffer = await file.arrayBuffer();
  const sha256 = await sha256Hex(buffer);

  let text: string;
  let pageCount: number;

  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    ({ text, pageCount } = await parsePdf(buffer));
  } else if (file.type.startsWith("text/") || file.name.toLowerCase().endsWith(".txt")) {
    text = new TextDecoder("utf-8").decode(buffer);
    pageCount = (text.match(/\f/g)?.length ?? 0) + 1;
  } else {
    // DOCX parsing lands with the ingest worker (Phase 2); reject clearly now.
    throw new Error(
      "Only PDF and plain-text files can be parsed in-browser today. DOCX support arrives with the ingest worker.",
    );
  }

  const wordCount = text.split(/\s+/).filter(Boolean).length;
  return { text, pageCount, wordCount, sha256 };
}
